import React from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import CardMedia from '@material-ui/core/CardMedia';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import { getImageUrl, hasValidImage } from './utils';

const useStyles = makeStyles((theme) => ({
  seriesCard: {
    cursor: 'pointer',
    '&:hover': {
      boxShadow: theme.shadows[8],
    },
  },
  media: {
    height: 200,
    backgroundSize: 'contain',
  },
  placeholder: {
    height: 200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f0f0f0',
    textAlign: 'center',
    padding: theme.spacing(2),
  },
  publisherBadge: {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    padding: '4px 8px',
    borderRadius: '4px',
    display: 'inline-block',
    marginBottom: theme.spacing(1),
  },
  yearBadge: {
    backgroundColor: theme.palette.grey[500],
    color: 'white',
    padding: '4px 8px',
    borderRadius: '4px',
    display: 'inline-block',
    marginLeft: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  description: {
    maxHeight: '60px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    '& p': {
      margin: 0,
    },
    '& a': {
      color: theme.palette.primary.main,
    },
  },
  issueCount: {
    marginTop: theme.spacing(1),
    fontStyle: 'italic',
  },
}));

// Fallback image for series without thumbnails
const PLACEHOLDER_IMAGE = 'https://via.placeholder.com/300x450.jpg';

const SeriesSelectionDialog = ({ open, seriesList, onSelect, onClose, hasMore, onLoadMore, totalResults }) => {
  const classes = useStyles();

  const handleSelect = (series) => {
    onSelect(series);
    onClose();
  };

  // Strip HTML tags and convert to plain text (safer than dangerouslySetInnerHTML)
  const stripHtmlTags = (html) => {
    if (!html) return '';
    // Use DOMParser for safer HTML parsing
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
    >
      <DialogTitle>Select a Series</DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          {seriesList.map((series) => {
            const hasImage = hasValidImage(series);
            const thumbnailUrl = hasImage 
              ? getImageUrl(series, 'portrait_medium')
              : PLACEHOLDER_IMAGE;
            
            return (
              <Grid item xs={12} sm={6} md={4} key={series.id}>
                <Card 
                  className={classes.seriesCard}
                  onClick={() => handleSelect(series)}
                >
                  {hasImage ? (
                    <CardMedia
                      className={classes.media}
                      image={thumbnailUrl}
                      title={series.title}
                    />
                  ) : (
                    <div className={classes.placeholder}>
                      <Typography variant="body2" color="textSecondary">
                        {series.title}
                      </Typography>
                    </div>
                  )}
                  <CardContent>
                    <Typography gutterBottom variant="h6" component="h2">
                      {series.title}
                    </Typography>
                    {series.publisher && (
                      <Typography 
                        variant="body2" 
                        className={classes.publisherBadge}
                      >
                        {series.publisher}
                      </Typography>
                    )}
                    {series.startYear && (
                      <Typography 
                        variant="body2" 
                        className={classes.yearBadge}
                      >
                        {series.startYear}{series.endYear && series.endYear !== series.startYear ? `-${series.endYear}` : ''}
                      </Typography>
                    )}
                    <Typography 
                      variant="body2" 
                      color="textSecondary" 
                      component="p"
                      className={classes.description}
                    >
                      {stripHtmlTags(series.description)}
                    </Typography>
                    {series.comics && series.comics.available > 0 && (
                      <Typography 
                        variant="body2" 
                        color="textSecondary"
                        className={classes.issueCount}
                      >
                        {series.comics.available} issues
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </DialogContent>
      <DialogActions style={{ justifyContent: 'space-between' }}>
        <Typography variant="body2" color="textSecondary" style={{ marginLeft: '16px' }}>
          Showing {seriesList.length}{totalResults !== undefined && totalResults !== null ? ` of ${totalResults}` : ''} results
        </Typography>
        <div>
          {hasMore && onLoadMore && (
            <Button onClick={onLoadMore} color="primary">
              More Results
            </Button>
          )}
          <Button onClick={onClose} color="primary">
            Cancel
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default SeriesSelectionDialog;
